import {StepCardProps} from '@features/assignment/components/step-card'
import i18next from '@/translations/i18n'
import {Assignment, HostAvailabilityDate, ServiceType} from '@features/assignment/types.ts'
import {PendingDescription} from '@features/assignment/components/assignment-descriptions/pending-description'
import {ReactElement} from 'react'
import {isPast, parse} from 'date-fns'
import {fromZonedTime} from 'date-fns-tz'
import {AcceptedDatesSharedDescription} from '@features/assignment/components/assignment-descriptions/accepted-dates-shared-description'
import {AcceptedDatesDeclinedDescription} from '@features/assignment/components/assignment-descriptions/accepted-dates-declined-description'
import {ScheduledDescription} from '@features/assignment/components/assignment-descriptions/scheduled-description'
import {AssetsReviewDescription} from '@features/assignment/components/assignment-descriptions/assets-review-description'
import {PendingPaymentDescription} from '@features/assignment/components/assignment-descriptions/pending-payment-description'
import {UploadAssetsDescription} from '@features/assignment/components/assignment-descriptions/upload-assets-description'
import {AcceptedDatesDontMatchDescription} from '@features/assignment/components/assignment-descriptions/accepted-dates-dont-match-description'
import {AcceptedFreelancerNotReadyDescription} from '@features/assignment/components/assignment-descriptions/accepted-freelancer-not-ready-description'
import {AcceptedHostNotReadyDescription} from '@features/assignment/components/assignment-descriptions/accepted-host-not-ready-description'
import {AcceptedHostUnresponsiveDescription} from '@features/assignment/components/assignment-descriptions/accepted-host-unresponsive-description'
import {AcceptedNoHostDatesDescription} from '@features/assignment/components/assignment-descriptions/accepted-no-host-dates-description'
import {NeedsMoreAssetsDescription} from '@features/assignment/components/assignment-descriptions/needs-more-assets-description'
import {FirstAcceptPendingDescription} from '@features/assignment/components/assignment-descriptions/first-accept-pending-description'
import {FirstAcceptNoLongerAvailableDescription} from '@features/assignment/components/assignment-descriptions/first-accept-no-longer-available-description'
import {ReviewCompletedDescription} from '@features/assignment/components/assignment-descriptions/review-completed-description'
import {formatLocaleDate, parseDate} from '@/dates.ts'

export const TASK_CATEGORY_HOTEL = 'Hotel' as const
export const TASK_CATEGORY_REVIEW = 'Reviewed By' as const

export const getServiceType = (
    taskMedia: Assignment['task_media']['name'],
    taskCategory: Assignment['task_category']['name']
): ServiceType => {
    if (taskCategory == TASK_CATEGORY_REVIEW) {
        return 'review'
    }
    if (taskMedia == 'matterport') {
        return 'matterport'
    }
    if (taskMedia == 'walkthrough') {
        return 'walkthrough'
    }
    if (taskMedia == 'video') {
        return 'experience_video'
    }
    return 'home'
}

// The stage an assignment is currently at, out of the 5 conceptual steps (decide, schedule, submit, review, pay).
// Every step before it is 'completed', every step after it is 'inactive'.
const getAssignmentStage = (
    assignment: Assignment
): {number: 1 | 2 | 3 | 4 | 5; status: 'active' | 'error' | 'completed'} => {
    if (
        (assignment.assignment_logic == 'First Accept' || assignment.task_category.name == TASK_CATEGORY_REVIEW) &&
        assignment.status == 'auto_cancelled'
    ) {
        return {number: 1, status: 'error'}
    }
    if (assignment.task_status.status == 'completed') {
        return {number: 5, status: 'completed'}
    }
    if (assignment.task_status.status == 'pending_payment') {
        return {number: 5, status: 'active'}
    }
    if (assignment.task_status.status == 'needs_more_assets') {
        return {number: 4, status: 'error'}
    }
    if (assignment.task_status.status == 'assets_in_review') {
        return {number: 4, status: 'active'}
    }
    if (assignment.task_status.status == 'scheduled' || assignment.task_status.status == 'pending_assets') {
        return {number: 3, status: 'active'}
    }
    if (assignment.status == 'accepted') {
        return {number: 2, status: 'active'}
    }
    return {number: 1, status: 'active'}
}

const getAssignmentStepTitles = (assignment: Assignment): string[] => {
    if (assignment.task_category.name == TASK_CATEGORY_REVIEW) {
        return [
            i18next.t('assignment:make_a_decision'),
            i18next.t('assignment:steps:step2:review'),
            i18next.t('assignment:steps:step3:review')
        ]
    }

    const serviceType = getServiceType(assignment.task_media.name, assignment.task_category.name)
    const serviceTypeVersion: ServiceType = serviceType == 'walkthrough' ? 'walkthrough' : 'home'

    return [
        i18next.t('assignment:make_a_decision'),
        i18next.t(`assignment:steps:step2:${serviceTypeVersion}`),
        serviceTypeVersion == 'walkthrough'
            ? i18next.t('assignment:submit_form')
            : i18next.t(`assignment:steps:step3:${serviceTypeVersion}`),
        i18next.t(`assignment:steps:step4:${serviceTypeVersion}`),
        assignment.is_paid_via_upwork && serviceTypeVersion == 'walkthrough'
            ? i18next.t('assignment:steps:step5_walkthrough_upwork')
            : i18next.t('assignment:steps:step5')
    ]
}

// Review assignments only surface the first 3 titles (decide, schedule, upload review); when the assignment's
// stage is further along (e.g. assets_in_review, pending_payment) than the last visible step, that step
// inherits the current stage's status instead, so progress made beyond it isn't lost visually.
export const getAssignmentSteps = (assignment: Assignment): Array<StepCardProps & {isCurrent: boolean}> => {
    const titles = getAssignmentStepTitles(assignment)
    const stage = getAssignmentStage(assignment)
    const currentStepNumber = Math.min(stage.number, titles.length)

    return titles.map((title, index) => {
        const number = index + 1
        if (number < currentStepNumber) {
            return {number, title, status: 'completed', isCurrent: false}
        }
        if (number == currentStepNumber) {
            return {number, title, status: stage.status, isCurrent: true}
        }
        return {number, title, status: 'inactive', isCurrent: false}
    })
}

export const getServiceTypeDescriptionsVersion = (
    taskMedia: Assignment['task_media']['name'],
    taskCategory: Assignment['task_category']['name']
): ServiceType => {
    const serviceType = getServiceType(taskMedia, taskCategory)
    return serviceType == 'walkthrough' || serviceType == 'review' ? serviceType : 'home'
}

export const getAssignmentDescription = (assignment: Assignment): ReactElement => {
    const serviceTypeVersion = getServiceTypeDescriptionsVersion(
        assignment.task_media.name,
        assignment.task_category.name
    )
    const isReview = serviceTypeVersion == 'review'

    if ((assignment.assignment_logic == 'First Accept' || isReview) && assignment.status == 'auto_cancelled') {
        return <FirstAcceptNoLongerAvailableDescription serviceType={serviceTypeVersion} />
    }
    if ((assignment.assignment_logic == 'First Accept' || isReview) && assignment.status == 'pending') {
        return <FirstAcceptPendingDescription assignment={assignment} serviceType={serviceTypeVersion} />
    }
    if (assignment.task_status.status == 'pending_payment') {
        return <PendingPaymentDescription assignment={assignment} serviceType={serviceTypeVersion} />
    }
    if (isReview && assignment.task_status.status == 'completed') {
        return <ReviewCompletedDescription />
    }
    // Review assignments have no in-app upload action, so the "pending_assets" stage already means the
    // report was submitted and shows the same copy as "assets_in_review".
    if (
        assignment.task_status.status == 'assets_in_review' ||
        (isReview && assignment.task_status.status == 'pending_assets')
    ) {
        return <AssetsReviewDescription assignment={assignment} serviceType={serviceTypeVersion} />
    }
    if (assignment.task_status.status == 'scheduled') {
        return <ScheduledDescription assignment={assignment} serviceType={serviceTypeVersion} />
    }
    if (assignment.task_status.status == 'pending_assets') {
        return <UploadAssetsDescription assignment={assignment} serviceType={serviceTypeVersion} />
    }
    if (assignment.task_status.status == 'needs_more_assets') {
        return <NeedsMoreAssetsDescription assignment={assignment} serviceType={serviceTypeVersion} />
    }

    if (assignment.task_status.coordination_status == 'dates_dont_match') {
        return <AcceptedDatesDontMatchDescription assignment={assignment} serviceType={serviceTypeVersion} />
    }
    if (assignment.task_status.coordination_status == 'photographer_not_available') {
        return <AcceptedFreelancerNotReadyDescription assignment={assignment} serviceType={serviceTypeVersion} />
    }
    if (assignment.task_status.coordination_status == 'host_not_ready') {
        return <AcceptedHostNotReadyDescription assignment={assignment} serviceType={serviceTypeVersion} />
    }
    if (assignment.task_status.coordination_status == 'host_unresponsive') {
        return assignment.host_responsive_at ? (
            <AcceptedDatesDeclinedDescription assignment={assignment} serviceType={serviceTypeVersion} />
        ) : (
            <AcceptedHostUnresponsiveDescription assignment={assignment} serviceType={serviceTypeVersion} />
        )
    }
    if (
        assignment.task_status.coordination_status == 'host_contacted' ||
        assignment.task_status.coordination_status == 'overdue_host_contacted'
    ) {
        return <AcceptedDatesDeclinedDescription assignment={assignment} serviceType={serviceTypeVersion} />
    }
    if (assignment.task_status.coordination_status == 'manual_coordination') {
        return <AcceptedNoHostDatesDescription assignment={assignment} serviceType={serviceTypeVersion} />
    }
    if (assignment.status == 'accepted') {
        return <AcceptedDatesSharedDescription assignment={assignment} serviceType={serviceTypeVersion} />
    }

    return <PendingDescription assignment={assignment} serviceType={serviceTypeVersion} />
}

export const formatHostAvailabilityDates = (startingTime?: string, endingTime?: string) =>
    startingTime && endingTime
        ? `${formatLocaleDate(startingTime, 'MMM dd')}: ${formatLocaleDate(startingTime, 'p')} - ${formatLocaleDate(endingTime, 'p')}`
        : '-'

// The date holds the listing's local date and time (not the browser's): true once that moment has passed
// in the listing's time zone. Without a known zone nothing is considered past, so nothing gets disabled.
export const isPastInListingTimeZone = (listingLocalDate: Date, timeZone?: string | null) =>
    !!timeZone && isPast(fromZonedTime(listingLocalDate, timeZone))

export const isTimeOfDayPastInListingTimeZone = (day: Date, time: string, timeZone?: string | null) =>
    isPastInListingTimeZone(parse(time, 'h:mm a', day), timeZone)

// A time of the picker is disabled once it has passed on the selected date in the listing's time zone. Admins can pick any.
export const checkIsTimeDisabled = ({
    time,
    date,
    timeZone,
    isAdmin
}: {
    time: string
    date?: Date
    timeZone?: string | null
    isAdmin: boolean
}) => !isAdmin && !!date && isTimeOfDayPastInListingTimeZone(date, time, timeZone)

export const sortHostAvailabilityDates = (dates: HostAvailabilityDate[]) => {
    return [...dates].sort((a, b) => parseDate(a.starting_time).getTime() - parseDate(b.starting_time).getTime())
}
